import React from 'react';
import { Skeleton } from '../atoms/skeleton';
import { Separator } from '../atoms/separator';

export interface InsightCard {
  title: string;
  description: string;
  solution: string;
}

export interface PainPoint {
  description: string;
}

export interface Persona {
  name: string;
}

export interface UIBestPractice {
  title: string;
  description: string;
}

export interface DesignInsightsData {
  problem_statement?: string;
  personas?: Persona[];
  pain_points?: PainPoint[];
  insights?: InsightCard[];
  ui_best_practices?: UIBestPractice[];
}

interface DesignInsightsPanelProps {
  open: boolean;
  onClose: () => void;
  inline?: boolean;
  children?: React.ReactNode;
  data?: DesignInsightsData;
  isLoading?: boolean;
}

export const DesignInsightsPanel: React.FC<DesignInsightsPanelProps> = ({
  open,
  onClose,
  inline = false,
  children,
  data,
  isLoading = false,
}) => {
  const d: DesignInsightsData = data || {};

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="px-6 py-4 space-y-6">
      {/* Problem Statement Skeleton */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-16 w-full" />
      </div>

      {/* Target Persona Skeleton */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-4 w-28" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-7 w-32 rounded-full" />
          <Skeleton className="h-7 w-28 rounded-full" />
        </div>
      </div>

      {/* Key Pain Points Skeleton */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-4 w-28" />
        </div>
        <div className="space-y-2 ml-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      </div>

      {/* Key Insights Skeleton */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border rounded-lg p-4 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (inline) {
    return (
      <div className="rounded-3xl m-2 w-[420px] max-w-full bg-background backdrop-blur flex flex-col relative z-10">
        <div className="sticky top-0 z-20 flex items-center justify-between px-6 py-4 rounded-tr-3xl rounded-tl-3xl glass-reflect glass-inner-light">
          <h2 className="text-lg font-semibold text-gray-900">Explore Design Insights</h2>
          <button
            onClick={onClose}
            aria-label="Close design insights"
            title="Close"
            className="ml-2 p-3 -mr-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <span className="sr-only">Close</span>
            <svg
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 20 20"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 6l8 8M6 14L14 6"
              />
            </svg>
          </button>
        </div>

        {/* Show loading skeleton or content */}
        {isLoading ? (
          <div className="flex-1 overflow-y-auto">
            <LoadingSkeleton />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* Problem Statement */}
            {d.problem_statement && (
              <>
                <section className="mb-6">
                  <h3 className="text-sm font-medium text-[#4B579F] flex items-center gap-2 mb-3">
                    <span>💁 Problem statement</span>
                  </h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{d.problem_statement}</p>
                </section>
                <Separator className="my-4" />
              </>
            )}

            {/* Target personas */}
            {d.personas && d.personas.length > 0 && (
              <>
                <section className="mb-6">
                  <h3 className="text-sm font-medium text-[#4B579F] flex items-center gap-2 mb-3">
                    <span>🙂 Tareget persona</span>
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {d.personas.map((persona, idx) => (
                      <div key={idx} className="w-full">
                        <span className="text-xs px-3 py-1 border rounded-full text-gray-700">
                          {persona.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
                <Separator className="my-4" />
              </>
            )}

            {/* Key pain points */}
            {d.pain_points && d.pain_points.length > 0 && (
              <>
                <section className="mb-6">
                  <h3 className="text-sm font-medium text-[#4B579F] flex items-center gap-2 mb-3">
                    <span>🥲 Key pain points</span>
                  </h3>
                  <ul className="ml-6 list-disc text-sm text-gray-700 space-y-1.5">
                    {d.pain_points.map((painPoint, idx) => (
                      <li key={idx}>{painPoint.description}</li>
                    ))}
                  </ul>
                </section>
                <Separator className="my-4" />
              </>
            )}

            {/* Design Insights */}
            {d.insights && d.insights.length > 0 && (
              <>
                <section className="mb-6">
                  <h3 className="text-sm font-medium text-[#4B579F] flex items-center gap-2 mb-3">
                    <span>🧠 Key insights from project context</span>
                  </h3>
                  <div className="space-y-4">
                    {d.insights.map((insight, idx) => (
                      <article
                        key={idx}
                        className="border rounded-lg bg-white p-3 text-sm text-gray-800"
                      >
                        <h4 className="font-semibold text-sm mb-2">{insight.title}</h4>
                        <div className="text-sm text-gray-700 leading-relaxed">
                          <p className="mb-2">
                            <span className="font-medium">Description:</span> {insight.description}
                          </p>
                          <p>
                            <span className="font-medium">Solution:</span> {insight.solution}
                          </p>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
                <Separator className="my-4" />
              </>
            )}

            {/* UI best practices */}
            {d.ui_best_practices && d.ui_best_practices.length > 0 && (
              <section className="mb-6">
                <h3 className="text-sm font-medium text-[#4B579F] flex items-center gap-2 mb-3">
                  <span>🧑‍💻 UI best practices</span>
                </h3>
                <div className="space-y-3">
                  {d.ui_best_practices.map((practice, idx) => (
                    <article
                      key={idx}
                      className="border rounded-lg bg-white p-3 text-sm text-gray-800"
                    >
                      <h4 className="font-semibold text-sm mb-2">{practice.title}</h4>
                      <div className="text-sm text-gray-700 leading-relaxed">
                        {practice.description}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {/* children (legacy / additional content) */}
            {children}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`fixed top-0 right-0 h-full w-[420px] max-w-full z-50 bg-white shadow-xl border-l flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}
      style={{ pointerEvents: open ? 'auto' : 'none' }}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <h2 className="text-lg font-semibold text-gray-900">Explore Design Insights</h2>
        <button onClick={onClose} className="ml-2 p-2 rounded hover:bg-gray-100">
          <span className="sr-only">Close</span>
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 20 20">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 6l8 8M6 14L14 6"
            />
          </svg>
        </button>
      </div>

      {/* Show loading skeleton or content */}
      {isLoading ? (
        <div className="flex-1 overflow-y-auto">
          <LoadingSkeleton />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
      )}
    </div>
  );
};

export default DesignInsightsPanel;
